import React, { useState, useEffect, useRef } from "react";
import "./Dashboard.css";
import { FaBars, FaTimes, FaPlus } from "react-icons/fa"; // Importing icons
import { FiBell } from "react-icons/fi";
import { auth } from "../firebase-config";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import SearchBox from "./SearchBox";
import HistoryPage from "./HistoryPage";
import UpgradePopup from "./UpgradePopup";
import companyTickers from "../company_tickers.json";
import PredictionOutput from "./PredictionOutput";
import Footer from "./Footer";
import { handleGoogleSignIn } from "./LoginPage";
import TrendingPage from "./TrendingPage";
import {
  FiTrendingUp,
  FiClock,
  FiPieChart,
  FiStar,
  FiUser,
  FiLogOut,
} from "react-icons/fi";
import AccountPage from "./AccountPage";
import axios from "axios";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import TickerTape from "./TickerTape";
import { logUserActivity } from "./logActivity";
import BottomDrawer from "./BottomDrawer";

const pageTypeMapping = {
  "/": "HME",
  "/home": "HME",
  "/dashboard/history": "HIS",
  "/analyze": "AZE",
  "/dashboard": "DASH",
  "/dashboard/trending": "TRE",
  "/progress": "PRO",
  "/dashboard/ai-picks": "SCR",
};

const baseURL =
  process.env.REACT_APP_ENVIRONMENT_TYPE === "DEV"
    ? "http://127.0.0.1:5000"
    : "https://flask-backend-52245432644.us-central1.run.app";

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
};

const calculateConfidence = (std_dev, predicted_change) => {
  // Handle undefined or invalid inputs
  if (
    std_dev === undefined ||
    predicted_change === undefined ||
    isNaN(std_dev) ||
    isNaN(predicted_change)
  ) {
    return 5;
  }

  const coefficient_of_variation = Math.abs(std_dev / predicted_change);
  let confidenceScore = 100 - coefficient_of_variation * 100;

  // Clamp confidence score between 5 and 95
  confidenceScore = Math.min(95, Math.max(5, confidenceScore));

  // Log the confidence calculation for debugging
  console.log("Confidence calculation:", {
    std_dev,
    predicted_change,
    coefficient_of_variation,
    confidenceScore,
  });

  return confidenceScore;
};

const calculateAccuracy = (accuracy) => {
  if (
    accuracy === undefined ||
    accuracy.toString() === "-1" ||
    Number(accuracy) < 0.05
  ) {
    return 5; // Return 5% as minimum
  }
  // Convert accuracy from 0-1 scale to 0-100 scale and ensure minimum of 5%
  return Math.max(5, Number(accuracy) * 100);
};

const getConfidenceCategory = (confidence) => {
  if (confidence <= 33) {
    return "Low";
  } else if (confidence <= 66) {
    return "Medium";
  } else {
    return "High";
  }
};

const parsePredictionData = (predictionData) => {
  try {
    const parsedData =
      typeof predictionData === "string"
        ? JSON.parse(predictionData)
        : predictionData;

    const std_dev = Number(parsedData.std_deviation);
    const predicted_change = Number(
      parsedData.next_day_change_percent.replace("%", "")
    );
    const confidence = calculateConfidence(std_dev, predicted_change);

    // Calculate meter position based on exact confidence percentage
    // The confidence meter gradient is 100px wide
    // Position the triangle directly based on the confidence percentage
    const confidenceMeterPosition = Math.min(Math.max(confidence, 0), 100);

    // For displaying the category text if needed
    const confidenceCategory = getConfidenceCategory(confidence);
    parsedData.confidence_category = confidenceCategory;

    // Calculate accuracy meter position using calculateAccuracy function
    const accuracyMeterPosition = calculateAccuracy(parsedData.accuracy);

    // Log the confidence data for debugging
    console.log("Confidence data:", {
      confidence,
      confidenceMeterPosition,
      accuracyMeterPosition,
      category: confidenceCategory,
    });

    return {
      parsedData,
      confidence,
      confidenceMeterPosition,
      accuracyMeterPosition,
    };
  } catch (error) {
    console.error("Error parsing prediction data:", error);
    return null;
  }
};

const returnPid = () => {
  const pid = (() => {
    const predictionData = localStorage.getItem("predictionData");
    if (predictionData) {
      try {
        return JSON.parse(predictionData)?.prediction_id || "unknown";
      } catch (error) {
        console.error("Error parsing predictionData from localStorage:", error);
        return "unknown";
      }
    } else {
      const currentUrl = window.location.href;
      return new URL(currentUrl).searchParams.get("pid") || "unknown";
    }
  })();
  return pid;
};

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [credits, setCredits] = useState("Loading...");
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [Flag, setFlag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [parsedPrediction, setParsedPrediction] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [progress, setProgress] = useState(0);
  const [hasFetched, setHasFetched] = useState(false);
  const [fetchError, setFetchError] = useState(false); // For tracking errors
  const [isMenuOpen, setMenuOpen] = useState(false); // State for mobile menu
  const navigate = useNavigate();
  const location = useLocation();
  const { prediction } = location.state || {};
  const [anonUserId, setAnonUserId] = useState(
    localStorage.getItem("anonUserId") || null
  );
  const [dayBeforeFormatted, setDayBeforeFormatted] = useState(null);
  const [weekBeforeFormatted, setWeekBeforeFormatted] = useState(null);
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false); // Popup visibility state
  const closeButtonRef = useRef(null);
  const disclaimerButtonRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [authLoading, setAuthLoading] = useState(false); // For authentication
  const [dataLoading, setDataLoading] = useState(false); // For data fetching
  const [progressLoading, setProgressLoading] = useState(false); // For progress bar simulation
  const params = new URLSearchParams(location.search);
  const [confidenceMeterMargin, setConfidenceMeterMargin] = useState(-7);
  const [accuracyMeterMargin, setAccuracyMeterMargin] = useState(0);
  const [isInFavorites, setIsInFavorites] = useState(false);
  const [streakInfo, setStreakInfo] = useState(null);
  const pid = params.get("pid"); // Get the prediction ID from URL
  const urlActiveTab = params.get("activeTab"); // Get the prediction ID from URL
  const ticker = params.get("t");
  const [activeTab, setActiveTab] = useState(urlActiveTab || "analyze");
  const eventSourceRef = useRef(null);
  const width = useWindowWidth();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isTooltipVisible, setTooltipVisible] = useState(false);
  const [shouldShowFullContentAnon, setShouldShowFullContentAnon] =
    useState(false);
  const [shouldShowFullContentAIPicks, setShouldShowFullContentAIPicks] =
    useState(false);
  const sentimentLeftMap = {
    "very bullish": 90,
    bullish: 75,
    neutral: 50,
    bearish: 25,
    "very bearish": 10,
  };
  const streakRewardMap = {
    2: 1,
    3: 2,
    5: 3,
    7: 5,
  };

  const updateUrlWithPid = (customUrl = window.location.href) => {
    const url = new URL(customUrl);
    let pid = url.searchParams.get("pid") || "unknown";

    if (pid === "unknown") {
      const newPid = returnPid();
      if (newPid && newPid !== "unknown") {
        url.searchParams.set("pid", newPid);
        window.history.pushState({}, document.title, url.toString());
      }
    }
  };

  const getStageNumber = () => {
    if (progress <= 49) return 1;
    if (progress <= 69) return 2;
    return 3;
  };

  const createAnonymousUser = async () => {
    try {
      // Fetch client IP address from an external service
      const ipResponse = await axios.get("https://api.ipify.org?format=json");
      const clientIp = ipResponse.data.ip;

      // Make a request to create an anonymous user with client IP in the headers
      const response = await axios.post(
        `${baseURL}/create-anon-user`,
        {},
        {
          headers: {
            "Client-IP": clientIp, // Add IP address to headers
          },
        }
      );

      const { user_id } = response.data;
      localStorage.setItem("anonUserId", user_id);
      setAnonUserId(user_id);
    } catch (error) {
      console.error("Error creating anonymous user:", error);
    }
  };

  const fetchCredits = async () => {
    const userId = localStorage.getItem("uid");
    const firebaseuid = localStorage.getItem("firebaseuid");
    if (!userId) {
      console.error("No user ID found in localStorage.");
      return;
    }
    if (!firebaseuid) {
      console.error("No firebase user ID found in localStorage.");
      return;
    }

    try {
      const response = await fetch(`${baseURL}/get-credits`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userId}:${firebaseuid}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch credits. Status: ${response.status}`);
      }

      const result = await response.json();
      setCredits(result.credits_available);
      localStorage.setItem("credits", result.credits_available); // Update localStorage
    } catch (error) {
      console.error("Error fetching credits:", error);
    }
  };

  const fetchStreak = async (userId) => {
    try {
      const baseURL =
        process.env.REACT_APP_ENVIRONMENT_TYPE === "DEV"
          ? "http://127.0.0.1:5000"
          : "https://flask-backend-52245432644.us-central1.run.app";

      const response = await axios.get(`${baseURL}/check-streak`, {
        params: { user_id: userId },
        headers: {
          "Content-Type": "application/json",
        },
      });
      const fetchedData = response.data;
      console.log(fetchedData);

      if (fetchedData) {
        setStreakInfo(fetchedData);
      } else {
        setStreakInfo(fetchedData);
        console.log("Invalid data format received");
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  useEffect(() => {
    const userId = localStorage.getItem("uid");
    let pageType = pageTypeMapping[location.pathname] || activeTab || "UNK";

    // If the "pid" query parameter is present, this is a "View Analysis" action
    const params = new URLSearchParams(location.search);
    if (params.has("pid") && location.pathname === "/dashboard") {
      pageType = "VWAN"; // View Analysis
    }
    logUserActivity(userId, pageType);
  }, [location.pathname]);

  useEffect(() => {
    const userId = localStorage.getItem("databaseUid");
    if (!userId) {
      console.error("No user ID found in localStorage.");
      setTimeout(() => {
        const userId = localStorage.getItem("databaseUid");
        if (!userId) {
          return;
        }
        fetchStreak(userId);
      }, 3000);
      return;
    }
    fetchStreak(userId);
  }, [user]);

  const signIn = async () => {
    setAuthLoading(true); // Set authentication loading state
    await handleGoogleSignIn(navigate, setLoading); // Handle Google sign-in

    try {
      const userId = localStorage.getItem("uid");
      const predictionId = localStorage.getItem("predictionId");

      if (!userId) {
        console.error("No user ID found in localStorage. Please sign in.");
        setAuthLoading(false);
        return;
      }

      const currentCredits = localStorage.getItem("credits");

      // Handle insufficient credits
      if (
        Number(currentCredits) <
        Number(process.env.REACT_APP_CREDITS_TO_SUBTRACT_ANALYZE)
      ) {
        localStorage.removeItem("predictionData");
        setFlag(true);
        setPopupOpen(true); // Show upgrade popup
        setStatusMessage("Insufficient credits. Please upgrade.");
        setAuthLoading(false);
        return;
      }

      // Continue if credits are sufficient

      const updatePredictionResponse = await fetch(
        `${baseURL}/update-prediction`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            prediction_id: predictionId,
            user_id: userId,
          }),
        }
      );

      if (updatePredictionResponse.ok) {
        const storedPredictionData = localStorage.getItem("predictionData");
        if (storedPredictionData) {
          const parsedPredictionData = JSON.parse(storedPredictionData);
          setApiData(JSON.parse(storedPredictionData));
          const result = parsePredictionData(parsedPredictionData.prediction);
          if (result) {
            setParsedPrediction(result.parsedData);
            console.log(result.parsedData);
            updateUrlWithPid();
            setConfidenceMeterMargin(result.confidenceMeterPosition);
            result.accuracyMeterPosition !== 0
              ? setAccuracyMeterMargin(result.accuracyMeterPosition)
              : setAccuracyMeterMargin(0);
            setStatusMessage("Prediction data loaded successfully!");
            await fetchCredits(); // Refresh credits after prediction update
            setActiveTab("analyze");
            navigate(`/dashboard`);
          }
        } else {
          console.error("No prediction data found in localStorage.");
        }
      } else {
        throw new Error("Failed to update prediction.");
      }
    } catch (error) {
      console.error("Error during sign-in process:", error);
      setStatusMessage(`Error during sign-in: ${error.message}`);
    } finally {
      setAuthLoading(false);
    }
  };

  const getCompanyTitle = (ticker) => {
    const company = Object.values(companyTickers).find(
      (item) => item.ticker === ticker.toUpperCase()
    );
    return company ? company.title : "Company Not Found";
  };

  useEffect(() => {
    if (performance.getEntriesByType("navigation")[0].type === "reload") {
      fetchCredits();
    }
    if (location.state?.showUpgradePopup) {
      setPopupOpen(true);
    }
    if (location.state?.refreshCredits) {
      fetchCredits();
    }
  }, [location.state]);

  useEffect(() => {
    if (
      !localStorage.getItem("uid") &&
      !localStorage.getItem("anonUserId") &&
      !pid
    ) {
      navigate("/"); // Redirect to login or another page
    } else {
      const storedCredits = localStorage.getItem("credits");
      if (storedCredits !== null) {
        setCredits(storedCredits);
      } else {
        setCredits(0);
      }
    }

    if (prediction && !hasFetched) {
      setApiData(prediction);
      console.log(prediction);
      const result = parsePredictionData(prediction.prediction);
      if (result) {
        setParsedPrediction(result.parsedData);
        console.log(result.parsedData);
        updateUrlWithPid();
        setConfidenceMeterMargin(result.confidenceMeterPosition);
        result.accuracyMeterPosition !== 0
          ? setAccuracyMeterMargin(result.accuracyMeterPosition)
          : setAccuracyMeterMargin(0);
        localStorage.setItem("predictionData", JSON.stringify(prediction));
        setHasFetched(true);
      }
    }
  }, [apiData, prediction, hasFetched, user, navigate, credits]);

  useEffect(() => {
    const storedValue = localStorage.getItem("isSubscribed");
    if (storedValue === "true") {
      setShouldShowFullContentAIPicks(true);
    } else {
      setShouldShowFullContentAIPicks(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
        setShouldShowFullContentAnon(true);
      } else {
        setUser(null);
        setShouldShowFullContentAnon(false);
      }
    });

    return () => unsubscribe();
  }, [shouldShowFullContentAnon, user]);

  const handleFetchData = async (ticker) => {
    if (!ticker) {
      alert("Please enter a ticker symbol");
      return;
    }
    if (
      Number(credits) <
      Number(process.env.REACT_APP_CREDITS_TO_SUBTRACT_ANALYZE)
    ) {
      setPopupOpen(true);
      return;
    }

    // Create a unique session key for this ticker
    const sessionKey = `predictionData_${ticker}`;

    // Remove old data for this ticker session
    localStorage.removeItem(sessionKey);

    // Clear states for a new fetch
    setApiData(null);
    setParsedPrediction(null);
    setConfidenceMeterMargin(0);
    setAccuracyMeterMargin(0);
    setStatusMessage("");
    setHasFetched(false);
    setFetchError(false);
    setProgressLoading(true);
    setProgress(0);
    setDataLoading(true);

    try {
      const userId = localStorage.getItem("uid") || anonUserId;
      const firebaseuid = localStorage.getItem("firebaseuid") || null;
      const authToken = firebaseuid ? `${userId}:${firebaseuid}` : userId;

      // Only get source from URL if we have a pid parameter
      const pid = params.get("pid");
      const source = pid
        ? params.get("source") || "discovery-referral"
        : "dashboard";

      setActiveTab("analyze");
      navigate(`/dashboard`, { replace: true });

      // Start listening to progress updates from the backend
      const eventSource = new EventSource(
        `${baseURL}/get-prediction?ticker=${ticker}&auth=${authToken}&source=${source}`
      );

      // Track the EventSource instance for this session
      if (!eventSourceRef.current) {
        eventSourceRef.current = {};
      }
      eventSourceRef.current[sessionKey] = eventSource;

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.error) {
          console.error("Error:", data.error);
          setFetchError(true);
          setStatusMessage(`Error: ${data.error}`);
          eventSource.close();
          setProgressLoading(false);
          return;
        }

        // Update progress and status message for this session
        if (data.progress !== undefined) {
          setProgress(data.progress);
          setProgressLoading(true);
        }

        if (data.status) {
          setStatusMessage(data.status);
        }

        if (data.progress === 100) {
          setProgressLoading(false);
        }

        // Final data from the backend
        if (data.data) {
          setApiData(data.data.prediction);
          localStorage.setItem(sessionKey, JSON.stringify(data.data));
          localStorage.setItem("predictionData", JSON.stringify(data.data)); // Store prediction data uniquely
          if (data.data.prediction) {
            const result = parsePredictionData(data.data.prediction);
            if (result) {
              setParsedPrediction(result.parsedData);
              updateUrlWithPid();
              setConfidenceMeterMargin(result.confidenceMeterPosition);
              result.accuracyMeterPosition !== 0
                ? setAccuracyMeterMargin(result.accuracyMeterPosition)
                : setAccuracyMeterMargin(0);
            }
          }
          setProgress(100);
          setStatusMessage("Prediction completed!");
          fetchCredits(); // Refresh credits
          eventSource.close();
        }
      };

      eventSource.onerror = (error) => {
        console.error("EventSource error:", error);
        setFetchError(true);
        setStatusMessage("Error fetching data. Please try again.");
        setProgressLoading(false);
        eventSource.close();
      };
    } catch (error) {
      console.error("Error fetching data:", error);
      setFetchError(true);
      setStatusMessage(`Error: ${error.message}`);
    } finally {
      setDataLoading(false);
      setProgressLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        Object.values(eventSourceRef.current).forEach((eventSource) =>
          eventSource.close()
        );
      }
    };
  }, []);

  useEffect(() => {
    if (pid && !user && !anonUserId) {
      // If there's a pid and the user is not logged in
      createAnonymousUser(); // Fetch the prediction data
    } else if (pid) {
      handleDisplayData(pid);
    }
  }, [location, user, anonUserId]); // Re-run if location or user status changes

  useEffect(() => {
    const checkIfInFavorites = async () => {
      if (!parsedPrediction) return;
      const userId = localStorage.getItem("databaseUid");
      try {
        const response = await axios.get(
          `${baseURL}/check-in-favorites?uid=${userId}&ticker=${parsedPrediction.ticker.toUpperCase()}`
        );
        setIsInFavorites(response.data.in_favorites);
      } catch (err) {
        console.log("Error: " + err);
      }
    };
    checkIfInFavorites();
  }, [parsedPrediction]);

  const handleDisplayData = async (prediction_id) => {
    setApiData(null);
    setParsedPrediction(null);
    setConfidenceMeterMargin(0);
    setAccuracyMeterMargin(0);
    localStorage.removeItem("predictionData");
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("predictionData_")) {
        localStorage.removeItem(key);
      }
    });

    try {
      let userID = user
        ? localStorage.getItem("uid")
        : localStorage.getItem("anonUserId");
      let firebase_uid = user ? user.uid : " ";
      const source = params.get("source") || "discovery-referral";
      const response = await fetch(`${baseURL}/get-prediction-json`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prediction_id: prediction_id,
          user_id: userID,
          firebase_uid: firebase_uid,
          is_anon: !user,
          source: source,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch data. Status: ${response.status}`);
      }
      const result = await response.json();
      if (user) {
        // If user is logged in, display full data
        setApiData(result.prediction_json[1]);
        setParsedPrediction(result.prediction_json[1]);
        console.log(result);
        updateUrlWithPid();
        // Handle confidence from backend or calculate it
        let confidenceValue;
        if (result.prediction_json[1].confidence_score) {
          // If backend provides confidence_score, use it directly
          confidenceValue = Number(result.prediction_json[1].confidence_score);

          // Set confidence category
          const category = getConfidenceCategory(confidenceValue);
          result.prediction_json[1].confidence_category = category;

          console.log("Using backend confidence:", {
            confidence: confidenceValue,
            category,
          });
        } else {
          // Otherwise calculate it from std_deviation
          confidenceValue = calculateConfidence(
            Number(result.prediction_json[1].std_deviation),
            Number(
              result.prediction_json[1].next_day_change_percent.replace("%", "")
            )
          );

          // Set confidence category
          const category = getConfidenceCategory(confidenceValue);
          result.prediction_json[1].confidence_category = category;
        }

        // Position triangle based on percentage (0-100)
        // For a 100px wide meter, the position in pixels directly corresponds to the percentage
        setConfidenceMeterMargin(confidenceValue);

        // Handle accuracy meter
        if (
          result.prediction_json[1].accuracy !== undefined &&
          result.prediction_json[1].accuracy.toString() !== "-1"
        ) {
          const accuracyValue = calculateAccuracy(
            result.prediction_json[1].accuracy
          );
          setAccuracyMeterMargin(accuracyValue);
        } else {
          setAccuracyMeterMargin(0);
        }

        await fetchCredits();
      } else {
        console.log(result.prediction_json[1].confidence_score);
        console.log(result.prediction_json[1].accuracy);
        result.prediction_json[1].confidence_score
          ? setConfidenceMeterMargin(result.prediction_json[1].confidence_score)
          : setConfidenceMeterMargin(0);
        result.prediction_json[1].accuracy
          ? setAccuracyMeterMargin(result.prediction_json[1].accuracy * 100)
          : setAccuracyMeterMargin(0);
        // If user is anonymous, display masked data
        const maskedData = {
          ...result.prediction_json[1],
          prediction:
            "Predicted data is hidden for anonymous users. Please log in to view details.",
        };
        setApiData(maskedData);
        setParsedPrediction(maskedData);
        console.log(maskedData);
        updateUrlWithPid();
      }
      setStatusMessage("Prediction data loaded successfully!");
      // Handle the response here (e.g., check response.ok and parse the JSON)
    } catch (error) {
      setFetchError(true);
      setStatusMessage(`Error fetching prediction data: ${error.message}`);
    } finally {
      setDataLoading(false);
      localStorage.removeItem("anonUserId");
    }
  };

  // Function to open the disclaimer popup
  const openDisclaimer = () => {
    setIsDisclaimerOpen(true);
    document.body.style.overflow = "hidden"; // Prevent background scrolling
  };

  // Function to close the disclaimer popup
  const closeDisclaimer = () => {
    setIsDisclaimerOpen(false);
    document.body.style.overflow = "auto"; // Re-enable background scrolling
    // Return focus to the disclaimer button
    if (disclaimerButtonRef.current) {
      disclaimerButtonRef.current.focus();
    }
  };

  const formatPredictionDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: "numeric", month: "long", day: "numeric" };
    return date.toLocaleDateString("en-US", options).split(",")[0];
  };

  // Handle Escape key to close the popup
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && isDisclaimerOpen) {
        closeDisclaimer();
      }
    };

    document.addEventListener("keydown", handleEscape);

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isDisclaimerOpen]);

  const toggleDropdown = () => {
    setIsDropdownOpen((prevState) => !prevState);
  };

  const handleLogout = async () => {
    try {
      // Clear all stored data in localStorage on logout
      localStorage.removeItem("uid"); // Remove user ID from localStorage
      localStorage.removeItem("predictionData"); // Remove prediction data from localStorage
      localStorage.clear();
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const handleAccount = async () => {
    setActiveTab("account");
    navigate("/dashboard/account");
  };

  const toggleMenu = () => {
    setMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const currentTab = location.pathname.split("/")[2] || "analyze";
    if (urlActiveTab === null) {
      setActiveTab(currentTab);
    }
  }, [location]);

  useEffect(() => {
    if (activeTab === "analyze") {
      // setApiData(null);
      // setParsedPrediction(null);
      setHasFetched(false);
      setFetchError(false);
      // localStorage.removeItem('predictionData');
    }
  }, [activeTab]);

  function formatNumber(num) {
    const absNum = Math.abs(Number(num));
    const sign = num < 0 ? "-" : "";
    const trillion = 1e12; // 1,000,000,000,000
    const billion = 1e9; // 1,000,000,000
    const million = 1e6; // 1,000,000

    if (absNum >= trillion) {
      return `${sign}${(absNum / trillion).toFixed(2)}T`;
    } else if (absNum >= billion) {
      return `${sign}${(absNum / billion).toFixed(2)}B`;
    } else if (absNum >= million) {
      return `${sign}${(absNum / million).toFixed(2)}M`;
    } else {
      return `${sign}${absNum.toLocaleString("en-US")}`;
    }
  }

  function processGptOutput(gptOutput) {
    const technicalAnalysisIndex = gptOutput.indexOf("Technical Analysis");

    if (technicalAnalysisIndex === -1) {
      console.warn("Technical Analysis section not found.");
      // Handle the absence of the section
      return {
        technicalAnalysis: "No technical analysis available.",
        // Other sections...
      };
    }

    // Existing logic to split and return sections...
  }

  function adjustLayout() {
    const menu = document.querySelector(".menu");
    const analysis = document.querySelector(".analysis");

    if (menu && analysis) {
      menu.style.marginBottom = "0";
      analysis.style.marginTop = "0";
    }
  }

  useEffect(() => {
    adjustLayout();
  }, []);

  return (
    <div className="logged-in-page">
      <Helmet>
        <title>Stocks: Advanced AI based Research and Prediction</title>
        <meta
          name="description"
          content="Use MarketCrunch's AI signals for stocks to buy"
        />
      </Helmet>
      <header className="header-dashboard">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          {/* Technical Difficulty Banner - Temporarily Hidden
          <div style={{ display: "flex", flexDirection: "row", width: "100%", justifyContent: "center", backgroundColor: "#ffcc00", padding: 5 }}>
            <span style={{ fontWeight: 600, fontFamily: "Inter", fontSize: 14 }}>
              We are experiencing technical difficulty pulling a Prediction right now. Contact <a href="mailto:support@marketcrunch.ai">support@marketcrunch.ai</a> for any questions
            </span>
          </div>
          */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%",
              padding: 20,
            }}
          >
            <div className="header-left">
              {/* Hamburger Menu Icon for Mobile */}
              <button
                className="hamburger-menu"
                onClick={toggleMenu}
                aria-label="Toggle navigation menu"
              >
                {isMenuOpen ? (
                  <FaTimes color="#1D3557" />
                ) : (
                  <FaBars color="#1D3557" />
                )}
              </button>

              <h3 style={{ display: "inline-block", position: "relative" }}>
                <a
                  href="/"
                  onClick={() =>
                    window.scrollTo({ top: 0, behavior: "smooth" })
                  }
                >
                  MarketCrunch AI
                  <span
                    style={{
                      fontSize: "0.75em",
                      position: "relative",
                      top: "-0.2em",
                    }}
                  >
                    &#8482;
                  </span>
                  <span
                    style={{
                      display: "block",
                      fontSize: "0.5em",
                      position: "absolute",
                      left: "75%",
                      top: "2.3em",
                      fontStyle: "italic",
                      letterSpacing: "0.05em",
                    }}
                  >
                    BETA
                  </span>
                </a>
              </h3>
            </div>

            <div className="header-right">
              {user ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-evenly",
                    padding: 10,
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                    background: "white",
                    borderRadius: 8,
                    position: "relative",
                    cursor: "pointer",
                  }}
                  onClick={() => setTooltipVisible(!isTooltipVisible)}
                  onMouseEnter={() => setTooltipVisible(true)} // For hover on desktop
                  onMouseLeave={() => setTooltipVisible(false)} // For hover on desktop
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-evenly",
                    }}
                  >
                    <i
                      className="fas fa-gift"
                      style={{
                        fontSize: 25,
                        marginRight: 7.5,
                        color: "#1D3557",
                      }}
                    ></i>
                    {streakInfo &&
                      (!streakInfo.can_claim_reward ||
                        (streakInfo.can_claim_reward &&
                          streakInfo.has_claimed_reward_today)) && (
                        <>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "space-evenly",
                            }}
                          >
                            {window.innerWidth < 600 && (
                              <div
                                style={{
                                  position: "relative",
                                  width: "30px",
                                  height: "30px",
                                }}
                              >
                                <svg
                                  width="30"
                                  height="30"
                                  viewBox="0 0 30 30"
                                  style={{
                                    position: "absolute",
                                    transform: "rotate(90deg)",
                                  }}
                                >
                                  <circle
                                    cx="15"
                                    cy="15"
                                    r="10"
                                    fill="none"
                                    stroke="#D1D1D1"
                                    strokeWidth="6"
                                  />
                                  <circle
                                    cx="15"
                                    cy="15"
                                    r="10"
                                    fill="none"
                                    stroke="#139C8D"
                                    strokeWidth="6"
                                    strokeDasharray={`${
                                      streakInfo.can_claim_reward &&
                                      streakInfo.has_claimed_reward_today
                                        ? (
                                            (streakInfo.streak /
                                              streakInfo.next_threshold) *
                                            100
                                          ).toFixed(0) *
                                          (62.83 / 100)
                                        : (
                                            (streakInfo.streak /
                                              streakInfo.current_threshold) *
                                            100
                                          ).toFixed(0) *
                                          (62.83 / 100)
                                    } 62.83`}
                                    strokeDashoffset="0"
                                  />
                                </svg>
                              </div>
                            )}
                            {window.innerWidth >= 600 && (
                              <div
                                style={{
                                  position: "relative",
                                  height: 20,
                                  borderRadius: 7.5,
                                  backgroundColor: "gray",
                                  width: "160px",
                                }}
                              >
                                <p
                                  style={{
                                    fontSize: 14,
                                    fontWeight: "600",
                                    fontFamily: "Inter",
                                    width: "100%",
                                    position: "absolute",
                                    zIndex: 100,
                                    textAlign: "center",
                                    marginTop: "2.5px",
                                    color: "white",
                                  }}
                                >
                                  {streakInfo.can_claim_reward &&
                                  streakInfo.has_claimed_reward_today
                                    ? (
                                        (streakInfo.streak /
                                          streakInfo.next_threshold) *
                                        100
                                      ).toFixed(0)
                                    : (
                                        (streakInfo.streak /
                                          streakInfo.current_threshold) *
                                        100
                                      ).toFixed(0)}
                                  %
                                </p>
                                <div
                                  style={{
                                    position: "absolute",
                                    height: "100%",
                                    left: 0,
                                    borderRadius: 7.5,
                                    backgroundColor: "#139C8D",
                                    width: `${
                                      streakInfo.can_claim_reward &&
                                      streakInfo.has_claimed_reward_today
                                        ? (
                                            (streakInfo.streak /
                                              streakInfo.next_threshold) *
                                            100
                                          ).toFixed(0)
                                        : (
                                            (streakInfo.streak /
                                              streakInfo.current_threshold) *
                                            100
                                          ).toFixed(0)
                                    }%`,
                                  }}
                                ></div>
                              </div>
                            )}
                            {window.innerWidth >= 600 && (
                              <p
                                style={{
                                  fontSize: 14,
                                  fontWeight: "500",
                                  fontFamily: "Inter",
                                  opacity: 0.75,
                                  marginTop: 2,
                                  marginBottom: 2,
                                }}
                              >
                                {streakInfo &&
                                streakInfo.days_left_for_reward != null
                                  ? streakInfo.can_claim_reward &&
                                    streakInfo.has_claimed_reward_today
                                    ? streakInfo.next_threshold -
                                      streakInfo.current_threshold
                                    : streakInfo.days_left_for_reward
                                  : "Error pulling streak!"}{" "}
                                day
                                {streakInfo &&
                                streakInfo.can_claim_reward &&
                                streakInfo.has_claimed_reward_today
                                  ? streakInfo.next_threshold -
                                      streakInfo.current_threshold >
                                    1
                                    ? "s"
                                    : ""
                                  : streakInfo.days_left_for_reward > 1
                                  ? "s"
                                  : ""}
                                {" until free credits!"}
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    {streakInfo &&
                      streakInfo.can_claim_reward &&
                      !streakInfo.has_claimed_reward_today && (
                        <>
                          <button
                            style={{
                              backgroundColor: "#139C8D",
                              color: "white",
                              border: "none",
                              borderRadius: "7.5px",
                              padding:
                                window.innerWidth <= 480
                                  ? "6px 12px"
                                  : "8px 16px",
                              cursor: "pointer",
                              fontWeight: "500",
                              fontFamily: "Inter, sans-serif",
                              whiteSpace: "nowrap",
                              fontSize:
                                window.innerWidth <= 480 ? "12px" : "14px",
                              transition: "all 0.2s ease-in-out",
                            }}
                            className="claim-rewards-button"
                            onClick={async () => {
                              try {
                                const baseURL =
                                  process.env.REACT_APP_ENVIRONMENT_TYPE ===
                                  "DEV"
                                    ? "http://127.0.0.1:5000"
                                    : "https://flask-backend-52245432644.us-central1.run.app";

                                const userId =
                                  localStorage.getItem("databaseUid");
                                const response = await fetch(
                                  `${baseURL}/claim-reward`,
                                  {
                                    method: "POST",
                                    headers: {
                                      "Content-Type": "application/json",
                                    },
                                    body: JSON.stringify({ user_id: userId }),
                                  }
                                );

                                const data = await response.json();

                                if (response.ok) {
                                  // Show confetti animation
                                  window.confetti({
                                    particleCount: 50,
                                    spread: 25,
                                    origin: { y: 0.2, x: 0.55 },
                                    zIndex: 1000,
                                  });

                                  // Update credits display
                                  fetchCredits();

                                  // Update streak info
                                  fetchStreak(userId);

                                  // Show appropriate success message
                                  if (data.streak_reset) {
                                    alert(
                                      `Congratulations! You've earned ${data.credits_awarded} credits! Your streak has been reset - start a new streak tomorrow!`
                                    );
                                  } else {
                                    alert(
                                      `Congratulations! You've earned ${data.credits_awarded} credits!`
                                    );
                                  }
                                } else {
                                  alert(data.error || "Failed to claim reward");
                                }
                              } catch (error) {
                                console.error("Error claiming reward:", error);
                                alert(
                                  "Failed to claim reward. Please try again."
                                );
                              }
                            }}
                          >
                            Claim Reward!
                          </button>
                        </>
                      )}
                  </div>
                </div>
              ) : (
                <button className="signin-button" onClick={signIn}>
                  Free Sign-up
                </button>
              )}
              {streakInfo && (
                <div
                  style={{
                    position: "absolute",
                    top: window.innerWidth > 400 ? "80px" : "60px",
                    right: window.innerWidth > 400 ? "20px" : "0px",
                    backgroundColor: "#139c8d",
                    color: "#fff",
                    padding: "5px 10px",
                    borderRadius: "4px",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                    zIndex: 10,
                    opacity: isTooltipVisible ? 1 : 0,
                    visibility: isTooltipVisible ? "visible" : "hidden",
                    transition: "opacity 0.2s ease, visibility 0.2s ease",
                    fontFamily: "Inter, sans-serif",
                    width: window.innerWidth > 400 ? "inherit" : "150%",
                  }}
                >
                  {streakInfo &&
                    ((!streakInfo.can_claim_reward &&
                      !streakInfo.has_claimed_reward_today) ||
                      (streakInfo.can_claim_reward &&
                        streakInfo.has_claimed_reward_today)) && (
                      <p
                        style={{
                          whiteSpace: "break-spaces",
                          width: "100%",
                          textAlign: "center",
                        }}
                      >
                        Visit daily! Get{" "}
                        {streakInfo &&
                        streakInfo.can_claim_reward &&
                        streakInfo.has_claimed_reward_today
                          ? streakRewardMap[Number(streakInfo.next_threshold)]
                          : streakRewardMap[
                              Number(streakInfo.current_threshold)
                            ]}{" "}
                        free credit
                        {streakInfo &&
                        streakInfo.can_claim_reward &&
                        streakInfo.has_claimed_reward_today
                          ? streakInfo.next_threshold > 1
                            ? "s"
                            : ""
                          : streakInfo.current_threshold > 1
                          ? "s"
                          : ""}{" "}
                        in{" "}
                        {streakInfo && streakInfo.days_left_for_reward != null
                          ? streakInfo.can_claim_reward &&
                            streakInfo.has_claimed_reward_today
                            ? streakInfo.next_threshold -
                              streakInfo.current_threshold
                            : streakInfo.days_left_for_reward
                          : "Error pulling streak!"}{" "}
                        day
                        {streakInfo &&
                        streakInfo.can_claim_reward &&
                        streakInfo.has_claimed_reward_today
                          ? streakInfo.next_threshold -
                              streakInfo.current_threshold >
                            1
                            ? "s"
                            : ""
                          : streakInfo.days_left_for_reward > 1
                          ? "s"
                          : ""}
                        !
                      </p>
                    )}
                  {streakInfo &&
                    streakInfo.can_claim_reward &&
                    !streakInfo.has_claimed_reward_today && (
                      <p>
                        Claim{" "}
                        {streakInfo &&
                        streakInfo.can_claim_reward &&
                        streakInfo.has_claimed_reward_today
                          ? streakRewardMap[Number(streakInfo.next_threshold)]
                          : streakRewardMap[
                              Number(streakInfo.current_threshold)
                            ]}{" "}
                        free credits now!
                      </p>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar Navigation - Visible on Desktop, Hidden on Mobile */}

      <div className={`sidebar-nav ${isMenuOpen ? "active" : ""}`}>
        <div className="nav-section">
          <div className="section-header" style={{ marginTop: 10 }}>
            SANDBOX
          </div>
          <button
            onClick={() => {
              setActiveTab("analyze");
              setMenuOpen(false);
              navigate("/dashboard");
            }}
            className={activeTab === "analyze" ? "active-tab" : ""}
          >
            <FiPieChart
              style={{
                marginRight: "8px",
                transition: "all 0.3s ease-in-out",
                color: activeTab === "analyze" ? "#139C8D" : "#374151",
              }}
            />
            Analyze
          </button>

          {user && (
            <>
              <button
                onClick={() => {
                  setActiveTab("alerts");
                  setMenuOpen(false);
                  navigate("/dashboard/alerts");
                }}
                className={activeTab === "alerts" ? "active-tab" : ""}
              >
                <FiBell
                  style={{
                    marginRight: "8px",
                    transition: "all 0.3s ease-in-out",
                    color: activeTab === "alerts" ? "#139C8D" : "#374151",
                  }}
                />{" "}
                Alerts
              </button>

              <button
                onClick={() => {
                  setActiveTab("ai-picks");
                  setMenuOpen(false);
                  navigate("/dashboard/ai-picks");
                }}
                className={activeTab === "ai-picks" ? "active-tab" : ""}
              >
                <FiStar
                  style={{
                    marginRight: "8px",
                    transition: "all 0.3s ease-in-out",
                    color: activeTab === "ai-picks" ? "#139C8D" : "#374151",
                  }}
                />{" "}
                AI Picks
              </button>

              <button
                onClick={() => {
                  setActiveTab("trending");
                  setMenuOpen(false);
                  navigate("/dashboard/trending");
                }}
                className={activeTab === "trending" ? "active-tab" : ""}
              >
                <FiTrendingUp
                  style={{
                    marginRight: "8px",
                    transition: "all 0.3s ease-in-out",
                    color: activeTab === "trending" ? "#139C8D" : "#374151",
                  }}
                />{" "}
                Trending
              </button>
            </>
          )}
        </div>

        {user && (
          <div className="nav-section">
            <div className="section-header">ACCOUNT</div>
            <button
              onClick={() => {
                setActiveTab("account");
                navigate("/dashboard/account");
              }}
              className={activeTab === "account" ? "active-tab" : ""}
            >
              <FiUser
                style={{
                  marginRight: "8px",
                  transition: "all 0.3s ease-in-out",
                  color: activeTab === "account" ? "#139C8D" : "#374151",
                }}
              />{" "}
              Profile
            </button>
            {user && (
              <button
                onClick={() => {
                  setActiveTab("history");
                  setMenuOpen(false);
                  navigate("/dashboard/history");
                }}
                className={activeTab === "history" ? "active-tab" : ""}
              >
                <FiClock
                  style={{
                    marginRight: "8px",
                    transition: "all 0.3s ease-in-out",
                    color: activeTab === "history" ? "#139C8D" : "#374151",
                  }}
                />{" "}
                History
              </button>
            )}
            <button
              onClick={handleLogout}
              className={activeTab === "logout" ? "active-tab" : ""}
            >
              <FiLogOut
                style={{
                  marginRight: "8px",
                  transition: "all 0.3s ease-in-out",
                  color: activeTab === "logout" ? "#139C8D" : "#374151",
                }}
              />{" "}
              Logout
            </button>
          </div>
        )}
      </div>
      {/* Overlay when mobile menu is open */}
      {isMenuOpen && <div className="overlay" onClick={toggleMenu}></div>}

      <div
        className="main-content"
        style={{
          maxHeight:
            (activeTab === "analyze" && shouldShowFullContentAnon) ||
            (activeTab === "trending") ||
            (activeTab === "history") ||
            (activeTab === "account") ||
            (activeTab === "alerts") ||
            (activeTab === "ai-picks" && shouldShowFullContentAIPicks)
              ? "100%"
              : activeTab === "ai-picks" && !shouldShowFullContentAIPicks
              ? "75vh"
              : "100vh",
          overflow:
            (activeTab === "analyze" && shouldShowFullContentAnon) ||
            (activeTab === "trending") ||
            (activeTab === "history") ||
            (activeTab === "account") ||
            (activeTab === "alerts") ||
            (activeTab === "ai-picks" && shouldShowFullContentAIPicks)
              ? "auto"
              : "hidden",
        }}
      >
        {user && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            <div className="header-container">
              <div style={{ width: "100%", marginBottom: "1em" }}>
                <TickerTape isLanding={false} />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexDirection: "row",
                  width: "100%",
                  gap: 20,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "start",
                    flexDirection: "column",
                    width: "50%",
                  }}
                >
                  <span
                    className="header-title"
                    style={{ fontFamily: "Merriweather, serif" }}
                  >
                    {activeTab === "ai-picks" ? "AI Picks" : activeTab}
                  </span>
                  <span
                    style={{
                      color: "gray",
                      fontSize: "16px",
                      marginTop: 5,
                      width: "160%",
                    }}
                  >
                    {activeTab === "analyze" && (
                      <>
                        Run an analysis or see what's{" "}
                        <Link
                          to="/dashboard/trending"
                          onClick={() => setActiveTab("trending")}
                          style={{ color: "#1D3557", fontWeight: 500 }}
                          className="trending-link"
                        >
                          trending
                        </Link>
                      </>
                    )}
                    {activeTab === "alerts" &&
                      "Set alerts for your favorite stocks and get notified of important changes."}
                    {activeTab === "ai-picks" &&
                      "See top tickers based on our AI engine's accuracy over last 90-day prediction performance."}
                    {activeTab === "trending" &&
                      `Analyze trending tickers. Each analysis costs ${process.env.REACT_APP_CREDITS_TO_SUBTRACT_TRENDING} credit.`}
                    {activeTab === "history" && "See your trading history"}
                    {activeTab === "account" && "Manage your account here"}
                  </span>
                </div>
                <div
                  style={{
                    width:
                      window.innerWidth < 485
                        ? "-webkit-fill-available"
                        : "30%",
                  }}
                >
                  <div className="credits-display-container">
                    <i
                      className="fas fa-wallet"
                      style={{ color: "#1D3557" }}
                    ></i>
                    <span className="credits-info">
                      {credits !== null ? credits : "Loading..."} credits
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "analyze" && user && (
          <div className="dashboard-search-container">
            <SearchBox
              onSearch={handleFetchData}
              loading={loading}
              buttonText="Analyze"
              isLanding={false}
            />
            {/* Welcome Message */}
            {!parsedPrediction && !dataLoading && !progressLoading && (
              <div className="welcome-message">
                <p>
                  Our advanced AI analyzes complex data from fundamentals,
                  technicals, and other sources to give you real, detailed, and
                  impactful insights.
                </p>
                <p>No hallucinations.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === "analyze" && (
          <>
            {progressLoading && (
              <div className="progress-bar-container">
                <div className="progress-bar" style={{ width: `${progress}%` }}>
                  <span className="progress-text">{progress}%</span>
                </div>
                <div className="animation-container-dashboard">
                  <div className="stage-indicator-container">
                    <div
                      className={`stage-indicator ${
                        getStageNumber() === 1 ? "active" : ""
                      }`}
                    >
                      <span>
                        Fetching Sources (
                        {parsedPrediction?.data_points
                          ? parsedPrediction.data_points
                          : "100K+"}
                        data points)
                      </span>
                    </div>
                    <div
                      className={`stage-indicator ${
                        getStageNumber() === 2 ? "active" : ""
                      }`}
                    >
                      <span>Generating Prediction</span>
                    </div>
                    <div
                      className={`stage-indicator ${
                        getStageNumber() === 3 ? "active" : ""
                      }`}
                    >
                      <span>Finalizing Results</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="blur-wrapper">
              {!shouldShowFullContentAnon && activeTab === "analyze" && (
                <BottomDrawer
                  isAnon={true}
                  parsedPrediction={parsedPrediction}
                  signIn={signIn}
                />
              )}

              {parsedPrediction && !dataLoading && !progressLoading && (
                <div
                  className={`prediction-data ${!user ? "blurred" : ""} ${
                    !Flag ? "" : "blurred"
                  }`}
                >
                  <PredictionOutput
                    gptOutput={parsedPrediction?.gpt_output}
                    positiveFeatures={parsedPrediction?.positive_features}
                    negativeFeatures={parsedPrediction?.negative_features}
                    apiData={parsedPrediction}
                    confidenceMeterMargin={confidenceMeterMargin}
                    accuracyMeterMargin={accuracyMeterMargin}
                    ticker={parsedPrediction?.ticker}
                    switchTab={setActiveTab}
                    user={user}
                  />
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === "history" && (
          <HistoryPage
            updateActiveTab={setActiveTab}
            handleDisplayData={handleDisplayData}
          />
        )}

        {activeTab === "trending" && (
          <TrendingPage
            updateActiveTab={setActiveTab}
            handleDisplayData={handleDisplayData}
          />
        )}

        {activeTab === "account" && <AccountPage setCredits={setCredits} />}
        <Outlet />
      </div>

      <UpgradePopup
        isOpen={isPopupOpen}
        handleClose={() => setPopupOpen(false)}
      />

      {user && <Footer />}
    </div>
  );
};

export default Dashboard;